"use client";

import Link from "next/link";
import { ArrowRight, Github, Link2 } from "lucide-react";
import { FaLaravel, FaReact } from "react-icons/fa6";
import { IoLogoFirebase } from "react-icons/io5";
import type { IconType } from "react-icons";
import {
	SiAndroid,
	SiArduino,
	SiDocker,
	SiExpress,
	SiFigma,
	SiAppstore,
	SiGooglecloud,
	SiGooglemaps,
	SiGoogleplay,
	SiInertia,
	SiJquery,
	SiKotlin,
	SiMapbox,
	SiMysql,
	SiNextdotjs,
	SiPrisma,
	SiRedux,
	SiTailwindcss,
	SiTensorflow,
	SiTypescript,
} from "react-icons/si";
import ProjectCtaLink from "@/components/site/project-cta-link";
import BrowserMockup from "@/components/site/browser-mockup";
import type { PublicPortfolioCardItem } from "@/lib/services/portfolio.service";

const TECH_ICON_MAP: Record<string, { icon: IconType; color?: string }> = {
	Laravel: { icon: FaLaravel },
	React: { icon: FaReact },
	"React Native": { icon: FaReact },
	"Tailwind CSS": { icon: SiTailwindcss },
	MySQL: { icon: SiMysql },
	TypeScript: { icon: SiTypescript },
	"Next.js": { icon: SiNextdotjs },
	Prisma: { icon: SiPrisma },
	"Redux Toolkit": { icon: SiRedux },
	"Express.js": { icon: SiExpress },
	Firebase: { icon: IoLogoFirebase },
	"Google Cloud SQL": { icon: SiGooglecloud },
	"Google Cloud Storage": { icon: SiGooglecloud },
	"Google Cloud Run": { icon: SiGooglecloud },
	"Google Maps API": { icon: SiGooglemaps },
	Android: { icon: SiAndroid },
	Docker: { icon: SiDocker },
	Kotlin: { icon: SiKotlin },
	TensorFlow: { icon: SiTensorflow },
	"Arduino IDE": { icon: SiArduino },
	Mapbox: { icon: SiMapbox },
	"Inertia.js": { icon: SiInertia },
	jQuery: { icon: SiJquery },
	Figma: { icon: SiFigma },
};

function TechIconBadge({ tech }: { tech: string }) {
	const entry = TECH_ICON_MAP[tech];

	if (!entry) {
		return (
			<span className="group/tech relative inline-flex">
				<span className="inline-flex h-8 w-8 items-center justify-center text-[0.66rem] font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:text-zinc-200">
					{tech
						.split(/\s+/)
						.map((part) => part[0])
						.join("")
						.slice(0, 2)
						.toUpperCase()}
				</span>
				<span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 rounded-[0.7rem] border border-white/8 bg-[#242426] px-3 py-2 text-[0.8rem] font-medium whitespace-nowrap text-zinc-100 opacity-0 shadow-[0_12px_30px_rgba(0,0,0,0.3)] transition duration-200 group-hover/tech:opacity-100">
					{tech}
					<span className="absolute bottom-0 left-1/2 h-3 w-3 -translate-x-1/2 translate-y-1/2 rotate-45 border-r border-b border-white/8 bg-[#242426]" />
				</span>
			</span>
		);
	}

	const Icon = entry.icon;

	return (
		<span className="group/tech relative inline-flex">
			<span className="inline-flex h-8 w-8 items-center justify-center text-[0.95rem] text-white transition duration-200 hover:-translate-y-0.5 hover:text-zinc-200">
				<Icon />
			</span>
			<span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 rounded-[0.7rem] border border-white/8 bg-[#242426] px-3 py-2 text-[0.8rem] font-medium whitespace-nowrap text-zinc-100 opacity-0 shadow-[0_12px_30px_rgba(0,0,0,0.3)] transition duration-200 group-hover/tech:opacity-100">
				{tech}
				<span className="absolute bottom-0 left-1/2 h-3 w-3 -translate-x-1/2 translate-y-1/2 rotate-45 border-r border-b border-white/8 bg-[#242426]" />
			</span>
		</span>
	);
}

function ProjectCardMockup({
	title,
	imageUrl,
}: {
	title: string;
	imageUrl: string;
}) {
	return (
		<div className="relative flex-1 overflow-hidden rounded-lg border border-dashed border-white/8 bg-[linear-gradient(180deg,#11141b_0%,#0b0d12_100%)]">
			<div className="absolute inset-x-6 top-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
			<div className="relative flex h-full items-center justify-center px-3 py-10">
				<BrowserMockup
					screenshot={{ title, imageUrl }}
					compact
					sizes="(max-width: 768px) 100vw, 280px"
					className="transition duration-500 group-hover:border-white/20 group-hover:shadow-[0_28px_70px_-24px_rgba(0,0,0,0.95)]"
					imageClassName="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
				/>
			</div>
		</div>
	);
}

export function SideProjectsList({
	portfolios,
}: {
	portfolios: PublicPortfolioCardItem[];
}) {
	return portfolios.map((portfolio, index) => {
		const imageOnRight = index % 2 === 0;

		return (
			<article key={portfolio.id} className="group py-3 sm:py-4">
				<div className="flex flex-col gap-5 md:flex-row md:items-stretch">
					<div
						className={`order-2 flex h-full min-h-[17.5rem] flex-1 flex-col rounded-xl border border-dashed border-white/8 p-6 sm:p-7 ${
							imageOnRight ? "md:order-1" : "md:order-2"
						}`}
					>
						<h2 className="font-rethink text-2xl font-semibold text-white transition group-hover:text-[#dfe5ef] sm:text-[1.95rem] sm:leading-tight">
							{portfolio.title}
						</h2>
						<p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-300 sm:text-base">
							{portfolio.description}
						</p>
						<div className="mt-5 flex flex-wrap gap-2.5">
							{portfolio.techStack.slice(0, 5).map((tech) => (
								<TechIconBadge key={`${portfolio.id}-${tech}`} tech={tech} />
							))}
						</div>
						<div className="mt-6 flex flex-col gap-4 pt-1 sm:flex-row sm:items-center">
							{portfolio.detail ? (
								portfolio.detail.startsWith("http") ? (
									<ProjectCtaLink href={portfolio.detail} external />
								) : (
									<ProjectCtaLink href={portfolio.detail} />
								)
							) : null}

							<div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm sm:ml-auto sm:justify-end">
								{portfolio.repository ? (
									<a
										href={portfolio.repository}
										target="_blank"
										rel="noopener noreferrer"
										className="group/repo inline-flex cursor-ne-resize cursor-apple-ne-resize items-center gap-1 text-[0.8rem] font-medium text-zinc-300 transition duration-300 hover:text-zinc-100 hover:underline hover:underline-offset-2"
									>
										<Github
											className="h-[0.78rem] w-[0.78rem] text-zinc-400 transition duration-300 group-hover/repo:text-[#dfe5ef]"
											strokeWidth={1.8}
										/>
										<span>Repository</span>
									</a>
								) : null}
								{portfolio.live ? (
									<a
										href={portfolio.live}
										target="_blank"
										rel="noopener noreferrer"
										className="group/live inline-flex cursor-ne-resize cursor-apple-ne-resize items-center gap-1 text-[0.8rem] font-medium text-zinc-300 transition duration-300 hover:text-zinc-100 hover:underline hover:underline-offset-2"
									>
										<Link2
											className="h-[0.76rem] w-[0.76rem] -rotate-[38deg] text-zinc-400 transition duration-300 group-hover/live:text-[#dfe5ef]"
											strokeWidth={1.9}
										/>
										<span>Open Live Site</span>
									</a>
								) : null}
								{portfolio.googlePlay ? (
									<a
										href={portfolio.googlePlay}
										target="_blank"
										rel="noopener noreferrer"
										className="group/google-play inline-flex cursor-ne-resize cursor-apple-ne-resize items-center gap-1 text-[0.8rem] font-medium text-zinc-300 transition duration-300 hover:text-zinc-100 hover:underline hover:underline-offset-2"
									>
										<SiGoogleplay className="h-[0.78rem] w-[0.78rem] text-zinc-400 transition duration-300 group-hover/google-play:text-[#dfe5ef]" />
										<span>Google Play</span>
									</a>
								) : null}
								{portfolio.appStore ? (
									<a
										href={portfolio.appStore}
										target="_blank"
										rel="noopener noreferrer"
										className="group/app-store inline-flex cursor-ne-resize cursor-apple-ne-resize items-center gap-1 text-[0.8rem] font-medium text-zinc-300 transition duration-300 hover:text-zinc-100 hover:underline hover:underline-offset-2"
									>
										<SiAppstore className="h-[0.78rem] w-[0.78rem] text-zinc-400 transition duration-300 group-hover/app-store:text-[#dfe5ef]" />
										<span>App Store</span>
									</a>
								) : null}
							</div>
						</div>
					</div>
					<div
						className={`flex min-h-[17.5rem] w-full rounded-xl border border-dashed border-white/8 p-3 md:w-[15rem] md:self-stretch lg:w-[17rem] ${
							imageOnRight ? "md:order-2" : "md:order-1"
						}`}
					>
						<ProjectCardMockup
							title={portfolio.title}
							imageUrl={portfolio.imageUrl}
						/>
					</div>
				</div>
			</article>
		);
	});
}

export default function SideProjectsSection({
	portfolios,
}: {
	portfolios: PublicPortfolioCardItem[];
}) {
	return (
		<div className="pt-0.5 sm:pt-1">
			<Link
				href="/projects/side-projects"
				className="group/side-card block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0d12]"
				aria-label="Open Side Projects page"
			>
				<div className="flex flex-col gap-5 md:flex-row md:items-stretch">
					<div className="relative min-h-[17.5rem] overflow-hidden rounded-xl border border-dashed border-white/8 bg-[linear-gradient(180deg,#11141b_0%,#0b0d12_100%)] md:w-[15rem] md:self-stretch lg:w-[17rem]">
						<div className="absolute inset-x-4 top-6 h-10 rounded-t-[1rem] border border-white/8 border-b-0" />
						<div className="absolute inset-x-4 top-15 bottom-0 rounded-t-[1rem] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.015),rgba(255,255,255,0.01))]" />
						<div className="absolute left-7 top-9 flex gap-2">
							<span className="h-3.5 w-3.5 rounded-full bg-white/10" />
							<span className="h-3.5 w-3.5 rounded-full bg-white/10" />
							<span className="h-3.5 w-3.5 rounded-full bg-white/10" />
						</div>
						<div className="absolute inset-x-0 bottom-8 text-center">
							<p className="font-serif text-[2.5rem] leading-[0.92] tracking-[-0.04em] text-white/92">
								Side
								<br />
								Project
							</p>
						</div>
					</div>
					<div className="flex min-h-[17.5rem] flex-1 flex-col rounded-xl border border-dashed border-white/8 p-6 sm:p-7">
						<p className="text-[0.7rem] font-medium uppercase tracking-[0.32em] text-zinc-500">
							Personal Archive
						</p>
						<h2 className="font-rethink mt-3 text-2xl font-semibold tracking-[-0.04em] text-white sm:text-[1.95rem] sm:leading-tight">
							Side Projects
						</h2>
						<p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-300 sm:text-base">
							Experiments, learning builds, and product ideas that I keep separate
							from client and institutional work.
						</p>
						<div className="mt-6 flex flex-col gap-4 pt-1 sm:flex-row sm:items-center">
							<div className="inline-flex w-fit items-center gap-3 overflow-hidden rounded-[1rem] border border-white/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-4 py-2 text-[0.95rem] text-zinc-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition duration-300 group-hover/side-card:border-white/18 group-hover/side-card:bg-white/[0.04]">
								<span>View Side Projects</span>
								<span className="inline-flex h-8 w-8 items-center justify-center rounded-[0.7rem] border border-white/10 bg-white/[0.05] text-zinc-100 transition duration-300 group-hover/side-card:border-white/14 group-hover/side-card:bg-white/[0.06]">
									<ArrowRight className="h-4 w-4 transition duration-300 group-hover/side-card:translate-x-0.5" />
								</span>
							</div>
							<span className="text-sm text-zinc-500 sm:ml-auto">
								{portfolios.length} project{portfolios.length > 1 ? "s" : ""}
							</span>
						</div>
					</div>
				</div>
			</Link>
		</div>
	);
}
